/** The two placeholders the catalog's titles carry, spelled exactly as `FORM_DATA_TABLE_2106`.
 *  The count leads the title as `X of Y` or as a bare `X`. */
const COUNT_PLACEHOLDER = /^X(?: of Y)?(?=\s)/
export const ATTENTION_OFFERING_PLACEHOLDER = 'Offering X'
/** A count that stands for plans — `X of Y plans …`, `X plans …` — rather than for partners. */
const PLAN_COUNT = /^X(?: of Y)?\s+plans?\b/i
/** The same count with the noun on its own, so it can be renamed. */
const PLAN_COUNT_NOUN = /^(X(?: of Y)?\s+)(plans?)\b/i

interface IFormatAttentionStatusTitleParams {
  title: string
  /** Rows the status stands for — the `X of Y`. Omitted for single-partner titles. The group it is
   *  counted against is stated by the level around the row, not inside every one of its titles. */
  raisedCount?: number
  /** What the count stands against — the `Y` of `X of Y`. Where it is given the title keeps the
   *  catalog's own `X of Y` wording, so a count of one still reads as a sentence. */
  scopeSize?: number
  /** Offering the title is stated for; replaces the `Offering X` placeholder. */
  offering?: string
}

/**
 * Fills a catalog title's placeholders. Multiple-partner rows read `35 partners …` and
 * single-partner rows read `Offering X …`; both are literal placeholders in the table, so the
 * widget substitutes them rather than the backend. A count placeholder with no count to fill is
 * dropped: the user never reads a bare `X`.
 */
export const formatAttentionStatusTitle = ({
  title,
  raisedCount,
  scopeSize,
  offering,
}: IFormatAttentionStatusTitleParams): string => {
  const withCount =
    raisedCount === undefined
      ? title.replace(COUNT_PLACEHOLDER, '').trimStart()
      : title.replace(COUNT_PLACEHOLDER, (placeholder) =>
          scopeSize && placeholder !== 'X' ? `${raisedCount} of ${scopeSize}` : String(raisedCount)
        )

  return offering ? withCount.replace(ATTENTION_OFFERING_PLACEHOLDER, offering) : withCount
}

/**
 * The same title with its count handed over on its own, so a row can set the number in its own
 * weight instead of burying it in the sentence. The catalog states the count first — `X of Y
 * partners …` — so the text is what follows it.
 */
export const splitAttentionStatusTitle = ({
  title,
  raisedCount,
  offering,
}: IFormatAttentionStatusTitleParams): { count?: number; text: string } => {
  const withOffering = offering ? title.replace(ATTENTION_OFFERING_PLACEHOLDER, offering) : title

  if (!COUNT_PLACEHOLDER.test(withOffering)) {
    return { text: withOffering }
  }

  return { count: raisedCount, text: withOffering.replace(COUNT_PLACEHOLDER, '').trimStart() }
}

/** Whether the title's count stands for plans — one per partner and offering — or for partners. */
export const isPlanCountedAttentionTitle = (title: string): boolean => PLAN_COUNT.test(title)

/**
 * The same title counting partners where it counted plans: `X plans …` reads `X partners …`.
 * A distributor plans all its offerings as one, so on its view one plan is one partner.
 */
export const renameAttentionPlanCount = (
  title: string,
  partnerNoun: { one: string; many: string }
): string =>
  title.replace(
    PLAN_COUNT_NOUN,
    (_match, count: string, noun: string) =>
      `${count}${noun.toLowerCase() === 'plan' ? partnerNoun.one : partnerNoun.many}`
  )
